import React from "react";
import { Box, Chip, Paper, Stack, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { useSearchParams } from "react-router-dom";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import FormatListBulletedRoundedIcon from "@mui/icons-material/FormatListBulletedRounded";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import PaletteRoundedIcon from "@mui/icons-material/PaletteRounded";
import FormatPaintRoundedIcon from "@mui/icons-material/FormatPaintRounded";
import DomainRoundedIcon from "@mui/icons-material/DomainRounded";
import PublicRoundedIcon from "@mui/icons-material/PublicRounded";
import PrecisionManufacturingRoundedIcon from "@mui/icons-material/PrecisionManufacturingRounded";
import WhiteLabelDashboardPage from "./WhiteLabelDashboardPage";
import WhiteLabelCompaniesPage from "../Companies/WhiteLabelCompaniesPage";
import WhiteLabelBrandingPage from "../Branding/WhiteLabelBrandingPage";
import WhiteLabelThemePage from "../Themes/WhiteLabelThemePage";
import WhiteLabelDomainsPage from "../Domains/WhiteLabelDomainsPage";
import WhiteLabelWebsitePage from "../Websites/WhiteLabelWebsitePage";
import WhiteLabelBuildCenterPage from "../BuildCenter/WhiteLabelBuildCenterPage";

export default function WhiteLabelRoutePage() {
  const theme = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const allowedSections = ["dashboard", "companies", "branding", "theme", "domains", "website", "build_center"];
  const requestedSection = String(searchParams.get("section") || "dashboard")
    .trim()
    .toLowerCase();
  const activeSection = allowedSections.includes(requestedSection)
    ? requestedSection
    : "dashboard";

  const heroGradient =
    theme.palette.mode === "dark"
      ? `linear-gradient(135deg, ${alpha(theme.palette.primary.dark, 0.38)} 0%, ${alpha("#0f172a", 0.96)} 100%)`
      : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)} 0%, ${alpha(theme.palette.primary.light, 0.22)} 100%)`;

  const handleSectionChange = (section) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("section", section);
    setSearchParams(nextParams, { replace: true });
  };

  return (
    <Stack spacing={3}>
      <Paper
        elevation={0}
        sx={{
          overflow: "hidden",
          borderRadius: 4,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.14)}`,
          background: heroGradient,
          boxShadow:
            theme.palette.mode === "dark"
              ? `0 24px 56px ${alpha("#020617", 0.34)}`
              : `0 24px 56px ${alpha(theme.palette.primary.main, 0.12)}`,
        }}
      >
        <Box sx={{ p: { xs: 2.5, md: 4 } }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "flex-start", md: "center" }}
            justifyContent="space-between"
          >
            <Stack spacing={1.25} sx={{ maxWidth: 820 }}>
              <Typography variant="h5" sx={{ fontWeight: 900 }}>
                White Label Management
              </Typography>
              <Typography variant="body1" sx={{ color: "text.secondary" }}>
                Manage dashboard insights and company operations from the
                existing Third Eye Telematics admin shell without changing the current
                navigation framework.
              </Typography>
            </Stack>
            <Chip
              label="Super Admin View"
              icon={<StorefrontRoundedIcon />}
              sx={{
                height: 40,
                fontWeight: 800,
                borderRadius: 999,
                px: 1,
                bgcolor:
                  theme.palette.mode === "dark"
                    ? alpha(theme.palette.common.white, 0.08)
                    : alpha(theme.palette.common.white, 0.82),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.16)}`,
                "& .MuiChip-icon": {
                  color: theme.palette.primary.main,
                },
              }}
            />
          </Stack>

          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 2.25 }}>
            {[
              {
                key: "companies",
                label: "Companies",
                icon: <FormatListBulletedRoundedIcon fontSize="small" />,
              },
              {
                key: "dashboard",
                label: "Dashboard",
                icon: <DashboardRoundedIcon fontSize="small" />,
              },
              {
                key: "branding",
                label: "Branding",
                icon: <PaletteRoundedIcon fontSize="small" />,
              },
              {
                key: "theme",
                label: "Theme",
                icon: <FormatPaintRoundedIcon fontSize="small" />,
              },
              {
                key: "domains",
                label: "Domains",
                icon: <DomainRoundedIcon fontSize="small" />,
              },
              {
                key: "website",
                label: "Website",
                icon: <PublicRoundedIcon fontSize="small" />,
              },
              {
                key: "build_center",
                label: "Build Center",
                icon: <PrecisionManufacturingRoundedIcon fontSize="small" />,
              },
            ].map((item) => {
              const active = activeSection === item.key;
              return (
                <Chip
                  key={item.key}
                  clickable
                  onClick={() => handleSectionChange(item.key)}
                  label={item.label}
                  icon={item.icon}
                  sx={{
                    height: 40,
                    px: 1,
                    borderRadius: 999,
                    fontWeight: 800,
                    bgcolor: active
                      ? theme.palette.primary.main
                      : theme.palette.mode === "dark"
                        ? alpha(theme.palette.common.white, 0.06)
                        : alpha(theme.palette.common.white, 0.82),
                    color: active ? theme.palette.primary.contrastText : theme.palette.text.primary,
                    border: `1px solid ${
                      active
                        ? alpha(theme.palette.primary.main, 0.95)
                        : alpha(theme.palette.primary.main, 0.14)
                    }`,
                    "& .MuiChip-icon": {
                      color: active
                        ? theme.palette.primary.contrastText
                        : theme.palette.primary.main,
                    },
                  }}
                />
              );
            })}
          </Stack>
        </Box>
      </Paper>

      {activeSection === "dashboard" ? (
        <WhiteLabelDashboardPage
          onNavigateSection={handleSectionChange}
        />
      ) : null}

      {activeSection === "companies" ? <WhiteLabelCompaniesPage /> : null}
      {activeSection === "branding" ? <WhiteLabelBrandingPage /> : null}
      {activeSection === "theme" ? <WhiteLabelThemePage /> : null}
      {activeSection === "domains" ? <WhiteLabelDomainsPage /> : null}
      {activeSection === "website" ? <WhiteLabelWebsitePage /> : null}
      {activeSection === "build_center" ? <WhiteLabelBuildCenterPage /> : null}
    </Stack>
  );
}
